import { axiosApiInstance } from "../axios";
import type {
  ProductPricingImportList,
  ProductPricingImportResponse,
  ProductPricingImportType,
} from "../types/productPricingImports";

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("authToken") ?? ""}` });

export class ProductPricingImportApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    public readonly response?: ProductPricingImportResponse
  ) {
    super(message);
    this.name = "ProductPricingImportApiError";
  }
}

function isApiError(error: unknown): error is {
  isAxiosError: true;
  response?: { data?: ProductPricingImportResponse; status?: number };
} {
  return Boolean(
    error &&
    typeof error === "object" &&
    "isAxiosError" in error &&
    (error as { isAxiosError?: unknown }).isAxiosError === true
  );
}

export async function listProductPricingImports(): Promise<ProductPricingImportList> {
  const response = await axiosApiInstance.get<ProductPricingImportList>("product-pricing-imports", {
    headers: headers(),
  });
  return response.data;
}

export async function uploadProductPricingImport(
  type: ProductPricingImportType,
  file: File
): Promise<ProductPricingImportResponse> {
  const body = new FormData();
  body.append("file", file);

  try {
    const response = await axiosApiInstance.post<ProductPricingImportResponse>(
      `product-pricing-imports/${type}`,
      body,
      { headers: headers() }
    );
    return response.data;
  } catch (error: unknown) {
    if (isApiError(error)) {
      throw new ProductPricingImportApiError(
        error.response?.data?.message || "The workbook could not be uploaded.",
        error.response?.status,
        error.response?.data
      );
    }
    throw new ProductPricingImportApiError("The workbook could not be uploaded.");
  }
}

export const uploadProductCosts = (file: File) => uploadProductPricingImport("cost", file);

export async function retryProductPricingImport(id: string): Promise<ProductPricingImportResponse> {
  try {
    const response = await axiosApiInstance.post<ProductPricingImportResponse>(
      `product-pricing-imports/${id}/retry`,
      {},
      { headers: headers() }
    );
    return response.data;
  } catch (error: unknown) {
    if (isApiError(error)) {
      throw new ProductPricingImportApiError(
        error.response?.data?.message || "The import could not be retried.",
        error.response?.status,
        error.response?.data
      );
    }
    throw new ProductPricingImportApiError("The import could not be retried.");
  }
}

async function download(path: string, fallbackFilename: string): Promise<void> {
  const response = await axiosApiInstance.get<Blob>(path, {
    headers: headers(),
    responseType: "blob",
  });
  const disposition = response.headers["content-disposition"] as string | undefined;
  const filename = disposition?.match(/filename="?([^";]+)"?/i)?.[1] || fallbackFilename;
  const url = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

const templateFilenames: Record<ProductPricingImportType, string> = {
  cost: "fluxorders-product-costs.xlsx",
  price: "fluxorders-product-prices.xlsx",
  "group-price": "fluxorders-group-prices.xlsx",
};

export const downloadProductPricingTemplate = (type: ProductPricingImportType) =>
  download(`product-pricing-imports/templates/${type}`, templateFilenames[type]);

export const downloadProductCostTemplate = () => downloadProductPricingTemplate("cost");

export const downloadProductPricingImportFile = (id: string, filename: string) =>
  download(`product-pricing-imports/${id}/file`, filename);
